import {memo} from "react";
import {Box, HStack, Icon, Separator, Stack, Strong, Text} from "@chakra-ui/react";
import {CalendarIcon, Square3Stack3DIcon, UserIcon} from "@heroicons/react/20/solid";
import {MenuContent, MenuRoot, MenuTrigger} from "../../../../../components/ui/menu";
import TagMenuButton from "../../../../../components/ui/tag-menu-button";
import type {Isa315ComponentName, RiskScore} from "../types";

const COMPONENT_LABELS: Record<Isa315ComponentName, string> = {
  complexity: "Complexity",
  subjectivity: "Subjectivity",
  change: "Change",
  uncertainty: "Uncertainty",
  fraudSusceptibility: "Fraud Susceptibility",
};

const COMPONENT_ORDER: Isa315ComponentName[] = [
  "complexity",
  "subjectivity",
  "change",
  "uncertainty",
  "fraudSusceptibility",
];

type ScoreThreshold = {label: string; color: string};

const SCORE_THRESHOLDS: ScoreThreshold[] = [
  {label: "Low", color: "green"},
  {label: "Moderate", color: "green"},
  {label: "Elevated", color: "yellow"},
  {label: "High", color: "red"},
  {label: "Very High", color: "red"},
];

function thresholdForScore(score: number): ScoreThreshold {
  return SCORE_THRESHOLDS[Math.min(Math.max(score, 1), 5) - 1];
}

const OVERALL_THRESHOLDS: ScoreThreshold[] = [
  {label: "Low", color: "green"},
  {label: "Low", color: "green"},
  {label: "Medium", color: "orange"},
  {label: "Significant", color: "red"},
  {label: "Significant", color: "red"},
];

function overallThreshold(score: number): ScoreThreshold {
  return OVERALL_THRESHOLDS[Math.min(Math.max(score, 1), 5) - 1];
}

const formatDate = (date: Date): string =>
  date.toLocaleDateString("en-GB", {day: "numeric", month: "short", year: "numeric"});

const RiskScorePopover = memo(({score, label = "Overall Risk"}: {score: RiskScore; label?: string}) => {
  const overallScore = Math.max(...Object.values(score.components));
  const overall = overallThreshold(overallScore);

  return (
    <MenuRoot lazyMount unmountOnExit positioning={{placement: "bottom"}} closeOnSelect={false} composite={false}>
      <MenuTrigger asChild>
        <TagMenuButton colorPalette={overall.color} gap={0.5}>
          <HStack gap={1.5}>
            {overall.label}
            <HStack bg="colorPalette.50" py={0.5} rounded="full" px={2} fontSize="xs" gap={0.5} fontWeight="bold">
              <Icon asChild boxSize="1em">
                <Square3Stack3DIcon />
              </Icon>
              {overallScore}
            </HStack>
          </HStack>
        </TagMenuButton>
      </MenuTrigger>
      <MenuContent p={0} w="sm">
        <Stack gap={0} separator={<Separator borderColor="gray.200" />}>
          {/* Overall risk row */}
          <HStack p={3} justify="space-between">
            <Text fontSize="sm" color="gray.700" fontWeight="semibold">
              {label}
            </Text>
            <TagMenuButton colorPalette={overall.color} size="xl" disabled>
              {overall.label}
              <Text as="span" ml={2} fontWeight="800" fontSize="xs">
                {overallScore}
              </Text>
            </TagMenuButton>
          </HStack>

          {/* Component scores */}
          <Stack p={3} bg="gray.50" gap={2}>
            {COMPONENT_ORDER.map(key => {
              const value = score.components[key];
              const threshold = thresholdForScore(value);
              return (
                <HStack key={key}>
                  <Text fontWeight="500" flex={1} fontSize="sm">
                    {COMPONENT_LABELS[key]}
                  </Text>
                  <HStack
                    py={0.5}
                    fontWeight="500"
                    rounded="full"
                    bg={`${threshold.color}.100`}
                    color={`${threshold.color}.fg`}
                    px={3}
                    fontSize="sm"
                    alignItems="baseline"
                  >
                    <Text>{threshold.label}</Text>
                    <Strong fontWeight="800" fontSize="xs">
                      {value}
                    </Strong>
                  </HStack>
                </HStack>
              );
            })}
          </Stack>

          {/* Rationale */}
          <Box p={3}>
            <Text color="gray.fg" fontSize="sm">
              {score.rationale}
            </Text>
          </Box>

          {/* Footer */}
          <Stack p={3} gap={1}>
            <HStack fontSize="xs" color="gray.500" gap={1.5}>
              <Icon asChild boxSize="1em">
                <UserIcon />
              </Icon>
              <Text>Assessed by {score.assessedBy}</Text>
            </HStack>
            <HStack fontSize="xs" color="gray.500" gap={1.5}>
              <Icon asChild boxSize="1em">
                <CalendarIcon />
              </Icon>
              <Text>{formatDate(score.assessedAt)}</Text>
            </HStack>
          </Stack>
        </Stack>
      </MenuContent>
    </MenuRoot>
  );
});

export default RiskScorePopover;
